<template>
  <div>
    <!-- Verification Modal -->
    <v-dialog 
      v-model="showModal" 
      max-width="500" 
      persistent
      :fullscreen="$vuetify.display.mobile"
      transition="dialog-bottom-transition"
      class="verification-modal"
    >
    <v-card class="verification-card" elevation="8">
      <!-- Close Button -->
      <v-btn
        icon="mdi-close"
        variant="text"
        size="small"
        class="close-btn"
        @click="closeModal"
      ></v-btn>

      <!-- Initial Information Screen -->
      <v-card-item v-if="currentStep === 0" class="initial-screen">
        <div class="text-center pt-6 pb-4">
          <v-img
            src="@/assets/verified-badge-new-blue.svg"
            height="80"
            width="80"
            class="mx-auto mb-4 badge-image"
            alt="Verification Badge"
          />
          <h2 class="text-h5 font-weight-bold mb-3 main-title">Get verified on Suuq!</h2>
          <div class="d-flex justify-center align-center mb-4 benefits-row">
            <v-chip color="primary" variant="outlined" size="small" class="mx-1">
              <v-icon start size="16">mdi-shield-check</v-icon>
              Build Trust
            </v-chip>
            <v-chip color="primary" variant="outlined" size="small" class="mx-1">
              <v-icon start size="16">mdi-eye</v-icon>
              Gain Visibility
            </v-chip>
            <v-chip color="primary" variant="outlined" size="small" class="mx-1">
              <v-icon start size="16">mdi-star</v-icon>
              Unlock Rewards
            </v-chip>
          </div>
        </div>
        
        <v-card class="feature-card mx-4 mb-4" elevation="1">
          <v-list class="py-2">
            <v-list-item class="feature-item">
              <template v-slot:prepend>
                <v-avatar color="green-lighten-4" size="40">
                  <v-icon color="green-darken-2">mdi-clock-fast</v-icon>
                </v-avatar>
              </template>
              <v-list-item-title class="font-weight-medium">Quick & Simple</v-list-item-title>
              <v-list-item-subtitle>It only takes a few minutes</v-list-item-subtitle>
            </v-list-item>
            
            <v-divider class="my-2"></v-divider>
            
            <v-list-item class="feature-item">
              <template v-slot:prepend>
                <v-avatar color="blue-lighten-4" size="40">
                  <v-icon color="blue-darken-2">mdi-shield-lock</v-icon>
                </v-avatar>
              </template>
              <v-list-item-title class="font-weight-medium">Secure</v-list-item-title>
              <v-list-item-subtitle>Your ID information stays private</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card>
        
        <div class="text-center mb-4">
          <v-btn
            variant="text"
            color="primary"
            @click="showLearnMore = true"
            class="learn-more-btn"
          >
            <v-icon start>mdi-information</v-icon>
            Learn more about verification on Suuq
          </v-btn>
        </div>
      </v-card-item>
      
      <!-- Verification Process Steps -->
      <v-window 
        v-if="currentStep > 0" 
        v-model="currentStep"
        class="verification-steps-window"
        :touch="{ left: nextStep, right: prevStep }"
      >
        <!-- Step 1: Log In -->
        <v-window-item :value="1" class="step-content">
          <div class="step-header">
            <v-chip color="primary" size="small" class="step-chip">Step 1 of 5</v-chip>
            <h3 class="text-h6 font-weight-bold text-center mt-3">Log in to your account</h3>
          </div>
          <div class="step-body text-center">
            <v-img 
              src="@/assets/verified-badge-new-blue.svg" 
              height="120" 
              width="120"
              class="mx-auto mb-4 step-image"
            />
            <v-card class="instruction-card mx-4" elevation="1">
              <v-card-text>
                <p class="mb-3">Log in to your Suuq account via the official website or mobile app.</p>
                <p class="text-body-2 text-medium-emphasis">
                  Look out for the 'Get Verified' pop-up or click on this verification section.
                </p>
              </v-card-text>
            </v-card>
          </div>
        </v-window-item>
        
        <!-- Step 2: OTP Verification -->
        <v-window-item :value="2" class="step-content">
          <div class="step-header">
            <v-chip color="primary" size="small" class="step-chip">Step 2 of 5</v-chip>
            <h3 class="text-h6 font-weight-bold text-center mt-3">Verify your phone number</h3>
          </div>
          <div class="step-body text-center">
            <v-img 
              src="@/assets/verified-badge-new-blue.svg" 
              height="120" 
              width="120"
              class="mx-auto mb-4 step-image"
            />
            <v-card class="instruction-card mx-4" elevation="1">
              <v-card-text>
                <p class="mb-3">Request a One Time Password (OTP) by entering your contact number.</p>
                <p class="mb-3">Enter the OTP to proceed with the verification process.</p>
                <v-alert color="warning" variant="tonal" class="text-start">
                  <v-icon start>mdi-shield-alert</v-icon>
                  <strong>Note:</strong> Keep your OTP private for account security.
                </v-alert>
              </v-card-text>
            </v-card>
          </div>
        </v-window-item>
        
        <!-- Step 3: ID Verification -->
        <v-window-item :value="3" class="step-content">
          <div class="step-header">
            <v-chip color="primary" size="small" class="step-chip">Step 3 of 5</v-chip>
            <h3 class="text-h6 font-weight-bold text-center mt-3">ID Verification</h3>
          </div>
          <div class="step-body text-center">
            <v-img 
              src="@/assets/verified-badge-new-blue.svg" 
              height="120" 
              width="120"
              class="mx-auto mb-4 step-image"
            />
            <v-card class="instruction-card mx-4" elevation="1">
              <v-card-text>
                <p class="mb-3">Scan your Emirates ID, including the front and back side of the ID.</p>
                <v-alert color="info" variant="tonal" class="text-start">
                  <v-icon start>mdi-information</v-icon>
                  Ensure clarity and validity of your Emirates ID for best results.
                </v-alert>
              </v-card-text>
            </v-card>
          </div>
        </v-window-item>
        
        <!-- Step 4: Face Verification -->
        <v-window-item :value="4" class="step-content">
          <div class="step-header">
            <v-chip color="primary" size="small" class="step-chip">Step 4 of 5</v-chip>
            <h3 class="text-h6 font-weight-bold text-center mt-3">Face Verification</h3>
          </div>
          <div class="step-body text-center">
            <v-img 
              src="@/assets/verified-badge-new-blue.svg" 
              height="120" 
              width="120"
              class="mx-auto mb-4 step-image"
            />
            <v-card class="instruction-card mx-4" elevation="1">
              <v-card-text>
                <p class="mb-3">Complete a quick facial recognition process.</p>
                <v-alert color="success" variant="tonal" class="text-start">
                  <v-icon start>mdi-lightbulb</v-icon>
                  Ensure you are in a well-lit place and your face is within the frame.
                </v-alert>
              </v-card-text>
            </v-card>
          </div>
        </v-window-item>
        
        <!-- Step 5: Profile Picture -->
        <v-window-item :value="5" class="step-content">
          <div class="step-header">
            <v-chip color="primary" size="small" class="step-chip">Step 5 of 5</v-chip>
            <h3 class="text-h6 font-weight-bold text-center mt-3">Upload Profile Picture</h3>
          </div>
          <div class="step-body text-center">
            <v-img 
              src="@/assets/verified-badge-new-blue.svg" 
              height="120" 
              width="120"
              class="mx-auto mb-4 step-image"
            />
            <v-card class="instruction-card mx-4" elevation="1">
              <v-card-text>
                <p>Complete the process by uploading your profile picture.</p>
                <v-alert color="primary" variant="tonal" class="text-start mt-3">
                  <v-icon start>mdi-camera</v-icon>
                  This will be your verified profile picture on Suuq.
                </v-alert>
              </v-card-text>
            </v-card>
          </div>
        </v-window-item>
        
        <!-- Final Confirmation -->
        <v-window-item :value="6" class="step-content completion-step">
          <div class="text-center py-6">
            <v-icon size="80" color="success" class="mb-4 success-icon">mdi-check-circle</v-icon>
            <h3 class="text-h5 font-weight-bold mb-3">Verification Complete!</h3>
            <v-card class="congrats-card mx-4" elevation="2" color="success-lighten-5">
              <v-card-text class="text-center">
                <h4 class="text-h6 mb-3">🎉 Congratulations!</h4>
                <p class="mb-0">Your verification is being processed. You'll receive your blue verification badge soon!</p>
              </v-card-text>
            </v-card>
          </div>
        </v-window-item>
      </v-window>

      <!-- Step Navigation -->
      <v-card-actions class="step-navigation pa-6">
        <template v-if="currentStep === 0">
          <v-btn 
            color="grey-darken-1" 
            variant="text" 
            @click="closeModal"
            class="flex-grow-1"
          >
            Later
          </v-btn>
          <v-btn 
            color="primary" 
            variant="elevated" 
            @click="startVerification"
            class="flex-grow-1 ml-4"
            size="large"
          >
            <v-icon start>mdi-shield-check</v-icon>
            Get Verified
          </v-btn>
        </template>
        
        <template v-if="currentStep > 0 && currentStep < 6">
          <v-btn 
            color="grey-darken-1" 
            variant="outlined" 
            @click="prevStep"
            :disabled="currentStep === 1"
            class="flex-grow-1"
          >
            <v-icon start>mdi-arrow-left</v-icon>
            Back
          </v-btn>
          <v-btn 
            color="primary" 
            variant="elevated" 
            @click="nextStep"
            class="flex-grow-1 ml-4"
            size="large"
          >
            Continue
            <v-icon end>mdi-arrow-right</v-icon>
          </v-btn>
        </template>
        
        <v-btn 
          v-if="currentStep === 6"
          color="success" 
          variant="elevated" 
          @click="completeVerification"
          block
          size="large"
        >
          <v-icon start>mdi-check</v-icon>
          Done
        </v-btn>
      </v-card-actions>

      <!-- Step Progress Indicator -->
      <div v-if="currentStep > 0 && currentStep < 6" class="step-progress pa-4">
        <v-progress-linear
          :model-value="(currentStep / 5) * 100"
          color="primary"
          height="6"
          rounded
          class="mb-3"
        ></v-progress-linear>
        <div class="d-flex justify-space-between">
          <v-tooltip 
            v-for="step in 5" 
            :key="step" 
            :text="`Step ${step}`" 
            location="bottom"
          >
            <template v-slot:activator="{ props }">
              <div 
                v-bind="props"
                class="step-dot" 
                :class="{ 
                  'step-active': currentStep >= step,
                  'step-current': currentStep === step 
                }"
              >
                <v-icon v-if="currentStep > step" size="12" color="white">mdi-check</v-icon>
                <span v-else-if="currentStep === step" class="step-number">{{ step }}</span>
              </div>
            </template>
          </v-tooltip>
        </div>
      </div>
    </v-card>
  </v-dialog>

  <!-- Learn More Dialog -->
  <v-dialog v-model="showLearnMore" max-width="900" scrollable>
    <v-card class="learn-more-card">
      <v-card-title class="pa-6 bg-primary-lighten-5">
        <div class="d-flex align-center">
          <v-icon color="primary" size="32" class="mr-3">mdi-check-decagram</v-icon>
          <div>
            <h2 class="text-h5 font-weight-bold">
              Verified <v-icon color="blue" size="24">mdi-check-decagram</v-icon> your blue tick to success!
            </h2>
          </div>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          @click="showLearnMore = false"
          class="position-absolute"
          style="top: 16px; right: 16px;"
        ></v-btn>
      </v-card-title>
      
      <v-card-text class="pa-6">
        <div class="intro-section mb-6">
          <p class="text-body-1 mb-4">
            Suuq, the UAE's premier classifieds platform, has always strived to foster trust among its users.
          </p>
          
          <p class="text-body-1 mb-4">
            Now, we're taking trust to a whole new level with 'Verified on Suuq' – a brilliant initiative 
            that ensures a safer and more secure platform for all.
          </p>
          
          <v-alert color="info" variant="tonal" class="mb-4">
            <v-icon start>mdi-star</v-icon>
            Earn the prestigious 'blue tick' and showcase the credibility of your account. 
            Being Verified on Suuq comes with more than just a badge!
          </v-alert>
        </div>

        <h3 class="text-h6 font-weight-bold mb-4">Why Get Yourself 'Verified on Suuq'</h3>
        
        <v-row class="mb-6">
          <v-col cols="12" md="4">
            <v-card elevation="2" class="pa-4 h-100 benefit-card" color="blue-lighten-5">
              <div class="text-center mb-3">
                <v-avatar size="56" color="blue-lighten-3">
                  <v-icon size="32" color="blue-darken-2">mdi-eye</v-icon>
                </v-avatar>
              </div>
              <h4 class="text-subtitle-1 text-center font-weight-bold mb-3">Boosted Visibility for Your Ads</h4>
              <p class="text-center text-body-2">Watch your engagement soar as Verified users receive top placement for their ads!</p>
            </v-card>
          </v-col>
          
          <v-col cols="12" md="4">
            <v-card elevation="2" class="pa-4 h-100 benefit-card" color="green-lighten-5">
              <div class="text-center mb-3">
                <v-avatar size="56" color="green-lighten-3">
                  <v-icon size="32" color="green-darken-2">mdi-check-decagram</v-icon>
                </v-avatar>
              </div>
              <h4 class="text-subtitle-1 text-center font-weight-bold mb-3">The Credibility of the Blue Tick</h4>
              <p class="text-center text-body-2">Enjoy the confidence of a Verified account with the distinguished blue tick.</p>
            </v-card>
          </v-col>
          
          <v-col cols="12" md="4">
            <v-card elevation="2" class="pa-4 h-100 benefit-card" color="orange-lighten-5">
              <div class="text-center mb-3">
                <v-avatar size="56" color="orange-lighten-3">
                  <v-icon size="32" color="orange-darken-2">mdi-headphones</v-icon>
                </v-avatar>
              </div>
              <h4 class="text-subtitle-1 text-center font-weight-bold mb-3">Priority Support</h4>
              <p class="text-center text-body-2">VIP treatment from our customer support team, with Verified users receiving priority assistance.</p>
            </v-card>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <h3 class="text-h6 font-weight-bold mb-4">How to Get Verified on Suuq</h3>
        
        <v-row class="verification-steps-info mb-6">
          <v-col cols="12" md="8">
            <v-timeline density="compact" class="verification-timeline">
              <v-timeline-item
                v-for="(step, index) in verificationSteps"
                :key="index"
                :dot-color="step.color"
                size="small"
              >
                <template v-slot:icon>
                  <v-icon size="16" color="white">{{ step.icon }}</v-icon>
                </template>
                <v-card elevation="1" class="pa-3 mb-2">
                  <h5 class="font-weight-bold mb-2">{{ step.title }}</h5>
                  <p class="text-body-2 mb-0">{{ step.description }}</p>
                  <em v-if="step.note" class="text-caption text-medium-emphasis">{{ step.note }}</em>
                </v-card>
              </v-timeline-item>
            </v-timeline>
          </v-col>
          
          <v-col cols="12" md="4" class="d-flex justify-center align-center">
            <v-img 
              src="@/assets/verified-badge-new-blue.svg" 
              max-width="200" 
              height="200"
              class="verification-logo"
            />
          </v-col>
        </v-row>
        
        <v-divider class="my-6"></v-divider>
        
        <h3 class="text-h6 font-weight-bold mb-4">Top Tips for a Smooth Verification Process</h3>
        
        <v-card color="grey-lighten-5" class="pa-4 mb-6">
          <v-list density="compact">
            <v-list-item
              v-for="(tip, index) in verificationTips"
              :key="index"
              class="px-0"
            >
              <template v-slot:prepend>
                <v-icon color="success" size="20">mdi-check-circle</v-icon>
              </template>
              <v-list-item-title class="text-body-2">{{ tip }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
        
        <div class="text-center mb-6">
          <v-btn 
            color="error" 
            size="x-large" 
            rounded
            @click="showVerificationModal"
            class="verification-cta-btn"
          >
            <v-icon start size="20">mdi-shield-check</v-icon>
            Get Verified NOW!
          </v-btn>
        </div>
        
        <v-alert color="primary" variant="tonal" class="text-center">
          <p class="mb-0">
            'Verified on Suuq' reinforces trust between our company and users, eliminating the risks of scams. 
            Join hundreds of thousands of Verified Suuq users today and don't lose out!
          </p>
        </v-alert>
      </v-card-text>
    </v-card>
  </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

// Props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
});

// Emits
const emit = defineEmits(['update:modelValue', 'verification-complete']);

// State
const showLearnMore = ref(false);
const currentStep = ref(0);

// Computed
const showModal = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// Data
const verificationSteps = [
  {
    title: 'Log in to your account',
    description: 'Log in to your Suuq account via the official website or mobile app. Look out for the \'Get Verified\' pop-up, or click on the verification section.',
    icon: 'mdi-login',
    color: 'blue'
  },
  {
    title: 'Request OTP',
    description: 'Request a One Time Password (OTP) by entering your contact number. Enter the OTP to proceed with the verification process.',
    note: 'Note: Keep your OTP private for account security.',
    icon: 'mdi-cellphone-message',
    color: 'green'
  },
  {
    title: 'Scan Emirates ID',
    description: 'Scan your Emirates ID, including the front and back side of the ID. Ensure clarity and validity of your Emirates ID.',
    icon: 'mdi-card-account-details',
    color: 'orange'
  },
  {
    title: 'Facial Recognition',
    description: 'Complete a quick facial recognition, ensure you are in a well-lit place and your face is within the frame.',
    icon: 'mdi-face-recognition',
    color: 'purple'
  },
  {
    title: 'Upload Profile Picture',
    description: 'Complete the process by uploading your profile picture.',
    icon: 'mdi-camera',
    color: 'red'
  }
];

const verificationTips = [
  'Use a valid, unexpired physical Emirates ID.',
  'Ensure the entire Emirates ID card is within the frame.',
  'Avoid any glares or reflections on your ID.',
  'Complete your facial recognition in a well-lit place for optimal results.',
  'Ensure your face is visible clearly during the facial recognition.'
];

// Methods
const closeModal = () => {
  showModal.value = false;
  currentStep.value = 0;
};

const startVerification = () => {
  currentStep.value = 1;
};

const nextStep = () => {
  if (currentStep.value < 6) {
    currentStep.value++;
  }
};

const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

const completeVerification = () => {
  emit('verification-complete');
  closeModal();
};

const showVerificationModal = () => {
  showLearnMore.value = false;
  showModal.value = true;
  currentStep.value = 0;
};

// Watch for modal close to reset step
watch(showModal, (newValue) => {
  if (!newValue) {
    currentStep.value = 0;
  }
});
</script>

<style scoped>
.verification-modal :deep(.v-dialog) {
  border-radius: 16px;
  overflow: hidden;
}

.verification-card {
  border-radius: 16px;
  position: relative;
  overflow: hidden;
}

.close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
}

.initial-screen {
  padding: 24px;
}

.badge-image {
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  transition: transform 0.3s ease;
}

.badge-image:hover {
  transform: scale(1.05);
}

.main-title {
  color: rgb(var(--v-theme-primary));
}

.benefits-row {
  gap: 8px;
}

.feature-card {
  border-radius: 12px;
  border: 1px solid rgba(var(--v-theme-primary), 0.2);
}

.feature-item {
  padding: 12px 16px;
}

.learn-more-btn {
  text-transform: none;
  font-weight: 500;
}

.verification-steps-window {
  min-height: 400px;
}

.step-content {
  padding: 24px 0;
}

.step-header {
  padding: 0 24px 16px;
  text-align: center;
}

.step-chip {
  background: linear-gradient(45deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary)));
  color: white;
}

.step-body {
  padding: 0 16px;
}

.step-image {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  transition: transform 0.3s ease;
}

.instruction-card {
  border-radius: 12px;
  border-left: 4px solid rgb(var(--v-theme-primary));
}

.completion-step {
  padding: 32px 0;
}

.success-icon {
  animation: bounce 0.5s ease-in-out;
}

@keyframes bounce {
  0%, 20%, 60%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  80% {
    transform: translateY(-5px);
  }
}

.congrats-card {
  border-radius: 12px;
  border: 2px solid rgb(var(--v-theme-success));
}

.step-navigation {
  background: rgba(var(--v-theme-surface), 0.8);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.1);
}

.step-progress {
  background: rgba(var(--v-theme-surface), 0.9);
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.05);
}

.step-dot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(var(--v-theme-on-surface), 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
}

.step-active {
  background: linear-gradient(45deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary)));
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(var(--v-theme-primary), 0.3);
}

.step-current {
  background: rgb(var(--v-theme-primary));
  animation: pulse 2s infinite;
}

.step-number {
  color: white;
  font-size: 12px;
  font-weight: bold;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(var(--v-theme-primary), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0);
  }
}

.learn-more-card {
  border-radius: 16px;
}

.benefit-card {
  border-radius: 12px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.benefit-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.verification-timeline :deep(.v-timeline-item__body) {
  padding-top: 0;
}

.verification-logo {
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1));
}

.verification-cta-btn {
  box-shadow: 0 4px 16px rgba(var(--v-theme-error), 0.4);
  transition: all 0.3s ease;
}

.verification-cta-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(var(--v-theme-error), 0.5);
}

/* Mobile responsiveness */
@media (max-width: 600px) {
  .verification-card {
    margin: 0;
    height: 100vh;
    border-radius: 0;
  }
  
  .step-content {
    padding: 16px 0;
  }
  
  .step-header {
    padding: 0 16px 12px;
  }
  
  .step-body {
    padding: 0 12px;
  }
  
  .step-navigation {
    padding: 16px 20px;
  }
  
  .initial-screen {
    padding: 16px;
  }
  
  .benefits-row {
    flex-direction: column;
    gap: 8px;
  }
  
  .benefits-row .v-chip {
    width: 100%;
    justify-content: center;
  }
}

/* Dark mode support */
.v-theme--dark .verification-card {
  background: rgb(var(--v-theme-surface));
}

.v-theme--dark .feature-card {
  background: rgba(var(--v-theme-surface-bright), 0.5);
}

.v-theme--dark .instruction-card {
  background: rgba(var(--v-theme-surface-bright), 0.3);
}
</style>